import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { DownloadIcon, EditIcon, PlusIcon, SettingsIcon, TrashIcon } from 'lucide-react';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { DataTable, type Column } from '@/components/custom/data-table';
import { tenantsApi } from '@/lib/api/tenants';
import type { Tenant } from '@/types/entities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';
import { toast } from 'sonner';
import { exportsApi } from '@/lib/api/exports';

export function AdminTenantsPage() {
  const navigate = useNavigate();
  usePageTitle('Tenants');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [databaseTarget, setDatabaseTarget] = useState<string>('main');
  const [isDownloadingDatabase, setIsDownloadingDatabase] = useState(false);

  const databaseOptions = useMemo(
    () => [
      { value: 'main', label: 'Main Database' },
      ...tenants.map((tenant) => ({
        value: `tenant:${tenant.slug}`,
        label: `Tenant: ${tenant.slug}`,
      })),
    ],
    [tenants]
  );

  const fetchTenants = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await tenantsApi.getAll();
      setTenants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = () => {
    navigate('/admin/dashboard/tenants/create');
  };

  const handleEdit = (tenant: Tenant) => {
    navigate(`/admin/dashboard/tenants/${tenant.id}/edit`);
  };

  const handleManage = (tenant: Tenant) => {
    navigate(`/admin/dashboard/tenants/${tenant.slug}`);
  };

  const handleDelete = (tenant: Tenant) => {
    setTenantToDelete(tenant);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!tenantToDelete) return;

    try {
      await tenantsApi.delete(tenantToDelete.id);
      setDeleteDialogOpen(false);
      setTenantToDelete(undefined);
      await fetchTenants();
      toast.success('Tenant deleted successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete tenant';
      setError(message);
      toast.error(message);
    }
  };

  const handleDownloadDatabase = async () => {
    setIsDownloadingDatabase(true);
    setError(null);
    try {
      if (databaseTarget === 'main') {
        await exportsApi.downloadMainDatabase();
        toast.success('Main database download started');
        return;
      }

      const tenantSlug = databaseTarget.replace('tenant:', '');
      await exportsApi.downloadTenantDatabase(tenantSlug);
      toast.success(`Tenant database (${tenantSlug}) download started`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to download database';
      setError(message);
      toast.error(message);
    } finally {
      setIsDownloadingDatabase(false);
    }
  };


  const columns: Column<Tenant>[] = [
    {
      key: 'id',
      header: 'ID',
      cell: (tenant) => <span className="text-muted-foreground">#{tenant.id}</span>,
      className: 'w-20',
      sortable: true,
      sortValue: (tenant) => tenant.id,
    },
    {
      key: 'name',
      header: 'Name',
      cell: (tenant) => tenant.name,
      sortable: true,
      sortValue: (tenant) => tenant.name,
    },
    {
      key: 'slug',
      header: 'Slug',
      cell: (tenant) => (
        <Badge variant="secondary" className="font-mono">
          {tenant.slug}
        </Badge>
      ),
      sortable: true,
      sortValue: (tenant) => tenant.slug,
    },
    {
      key: 'website_url',
      header: 'Website URL',
      cell: (tenant) => (
        <a
          href={tenant.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline truncate block max-w-[200px]"
        >
          {tenant.website_url}
        </a>
      ),
      sortable: true,
      sortValue: (tenant) => tenant.website_url,
    },
    {
      key: 'created_at',
      header: 'Created',
      cell: (tenant) => new Date(tenant.created_at).toLocaleDateString(),
      className: 'w-32',
      sortable: true,
      sortValue: (tenant) => new Date(tenant.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      cell: (tenant) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleManage(tenant)}
            className="h-8 w-8"
            title="Manage"
          >
            <SettingsIcon className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(tenant)}
            className="h-8 w-8"
            title="Edit"
          >
            <EditIcon className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDelete(tenant)}
            className="h-8 w-8 text-destructive hover:text-destructive"
            title="Delete"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
      className: 'w-32',
    },
  ];

  return (
    <>
      <PageHeader
        title="Tenants"
        description="Manage platform tenants"
      />

      <PageSection>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-destructive">
            {error}
          </div>
        )}

        <DataTable
          data={tenants}
          columns={columns}
          searchKeys={['name', 'slug']}
          searchPlaceholder="Search by name or slug..."
          actions={
            <div className="flex items-center gap-2">
              <Select value={databaseTarget} onValueChange={setDatabaseTarget}>
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Select database" />
                </SelectTrigger>
                <SelectContent>
                  {databaseOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handleDownloadDatabase}
                disabled={isDownloadingDatabase}
              >
                <DownloadIcon className="mr-2 size-4" />
                {isDownloadingDatabase ? 'Downloading...' : 'Download DB'}
              </Button>
              <Button onClick={handleCreate}>
                <PlusIcon className="mr-2 size-4" />
                Add Tenant
              </Button>
            </div>
          }
          emptyMessage="No tenants found"
          isLoading={isLoading}
        />
      </PageSection>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Tenant"
        description={`Are you sure you want to delete "${tenantToDelete?.name}"? This will also delete all associated data and cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />
    </>
  );
}
